import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTutorial } from '../contexts/TutorialContext';
import { TutorialStep } from '../config/tutorials';
import '../styles/Tutorial.css';

/**
 * TutorialOverlay Component
 *
 * Main tutorial renderer that displays an interactive overlay with:
 * - Semi-transparent backdrop
 * - Spotlight highlight on target UI elements
 * - Floating content modal with step instructions
 * - Navigation controls (Next, Previous, Skip)
 */
const TutorialOverlay: React.FC = () => {
  const {
    activeTutorial,
    currentStep: stepIndex,
    isActive,
    nextStep,
    previousStep,
    skipTutorial
  } = useTutorial();

  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);
  const [modalPosition, setModalPosition] = useState<{ top: number; left: number } | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const currentStepData: TutorialStep | undefined = activeTutorial?.steps[stepIndex];

  /**
   * Calculate spotlight and modal positions based on target element
   */
  const updatePositions = useCallback(() => {
    if (!currentStepData) return;

    // If no target selector, this is a center-screen info step
    if (!currentStepData.targetSelector) {
      setSpotlightRect(null);
      setModalPosition(null);
      return;
    }

    try {
      const targetElement = document.querySelector(currentStepData.targetSelector);

      if (!targetElement) {
        console.warn(`Tutorial target not found: ${currentStepData.targetSelector}`);
        setSpotlightRect(null);
        setModalPosition(null);
        return;
      }

      const rect = targetElement.getBoundingClientRect();

      // Set spotlight rectangle
      setSpotlightRect(rect);

      // Calculate modal position based on position preference
      const position = currentStepData.position || 'bottom';
      const modalWidth = 400; // Max width from CSS
      const modalHeight = 300; // Estimated height
      const gap = 20; // Gap between spotlight and modal

      let top = 0;
      let left = 0;

      switch (position) {
        case 'top':
          top = rect.top - modalHeight - gap;
          left = rect.left + rect.width / 2 - modalWidth / 2;
          break;
        case 'bottom':
          top = rect.bottom + gap;
          left = rect.left + rect.width / 2 - modalWidth / 2;
          break;
        case 'left':
          top = rect.top + rect.height / 2 - modalHeight / 2;
          left = rect.left - modalWidth - gap;
          break;
        case 'right':
          top = rect.top + rect.height / 2 - modalHeight / 2;
          left = rect.right + gap;
          break;
        case 'center':
          // Center of viewport
          top = window.innerHeight / 2 - modalHeight / 2;
          left = window.innerWidth / 2 - modalWidth / 2;
          break;
      }

      // Ensure modal stays within viewport
      top = Math.max(20, Math.min(top, window.innerHeight - modalHeight - 20));
      left = Math.max(20, Math.min(left, window.innerWidth - modalWidth - 20));

      setModalPosition({ top, left });
    } catch (error) {
      console.error('Error calculating tutorial positions:', error);
      setSpotlightRect(null);
      setModalPosition(null);
    }
  }, [currentStepData]);

  // Update positions when step changes or window resizes
  useEffect(() => {
    if (!isActive) return;

    updatePositions();

    const handleResize = () => updatePositions();
    window.addEventListener('resize', handleResize);

    // Re-calculate positions after a short delay to ensure DOM is ready
    const timer = setTimeout(updatePositions, 100);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, [isActive, stepIndex, currentStepData, updatePositions]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'Enter':
          e.preventDefault();
          nextStep();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          previousStep();
          break;
        case 'Escape':
          e.preventDefault();
          skipTutorial();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, nextStep, previousStep, skipTutorial]);

  if (!isActive || !activeTutorial || !currentStepData) {
    return null;
  }

  const progress = ((stepIndex + 1) / activeTutorial.steps.length) * 100;
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === activeTutorial.steps.length - 1;

  // Determine if modal should be centered
  const isCentered = !currentStepData.targetSelector || currentStepData.position === 'center';

  return (
    <div className="tutorial-overlay" ref={overlayRef}>
      {/* Dark backdrop */}
      <div className="tutorial-backdrop" onClick={skipTutorial} />

      {/* Spotlight on target element */}
      {spotlightRect && (
        <div
          className="tutorial-spotlight"
          style={{
            top: spotlightRect.top - (currentStepData.highlightPadding || 8),
            left: spotlightRect.left - (currentStepData.highlightPadding || 8),
            width: spotlightRect.width + (currentStepData.highlightPadding || 8) * 2,
            height: spotlightRect.height + (currentStepData.highlightPadding || 8) * 2
          }}
        />
      )}

      {/* Tutorial content modal */}
      <div
        className={`tutorial-content-modal ${
          isCentered ? 'tutorial-modal-center' : ''
        }`}
        style={
          isCentered
            ? undefined
            : modalPosition
            ? { top: modalPosition.top, left: modalPosition.left }
            : undefined
        }
      >
        {/* Header */}
        <div className="tutorial-modal-header">
          <h3 className="tutorial-modal-title">{currentStepData.title}</h3>
          <button
            className="tutorial-modal-close"
            onClick={skipTutorial}
            aria-label="Close tutorial"
          >
            ×
          </button>
        </div>

        {/* Progress indicator */}
        <div className="tutorial-progress">
          <span className="tutorial-progress-text">
            Step {stepIndex + 1} of {activeTutorial.steps.length}
          </span>
          <div className="tutorial-progress-bar">
            <div className="tutorial-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Step indicator dots */}
        <div className="tutorial-step-dots">
          {activeTutorial.steps.map((_, index) => (
            <div
              key={index}
              className={`tutorial-step-dot ${
                index === stepIndex ? 'active' : index < stepIndex ? 'completed' : ''
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="tutorial-modal-content">{currentStepData.content}</div>

        {/* Interaction hint */}
        {currentStepData.interactionHint && (
          <div className="tutorial-interaction-hint">{currentStepData.interactionHint}</div>
        )}

        {/* Footer with navigation buttons */}
        <div className="tutorial-modal-footer">
          <button
            className="tutorial-btn tutorial-btn-skip"
            onClick={skipTutorial}
          >
            Skip
          </button>

          <button
            className="tutorial-btn tutorial-btn-secondary"
            onClick={previousStep}
            disabled={isFirstStep}
          >
            Previous
          </button>

          <button
            className="tutorial-btn tutorial-btn-primary"
            onClick={nextStep}
          >
            {isLastStep ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TutorialOverlay;
